import React, { useState, useRef, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { PlusCircle, X } from 'react-feather';
import AddressAutocomplete from './AddressAutocomplete';
import toast from 'react-hot-toast';
import FacilityDurationFollowUpNotice from './FacilityDurationFollowUpNotice';
import {
  calculateActualDurationMinutes,
  durationExceedsScheduled,
  FACILITY_DURATION_FOLLOW_UP_MESSAGE,
} from '../utils/completionReportDuration';
import { getApiUrl } from '../runtimeEnv';

const API_BASE = getApiUrl();

const resultOptions = [
  { label: "Completed", value: "Completed" },
  { label: "Completed with follow up", value: "Completed with follow up" },
  { label: "Completed + patient arrived earlier", value: "Completed + patient arrived earlier" },
  { label: "Claimant No Show", value: "Claimant No Show" },
  { label: "Provider Missed Appointment", value: "Provider Missed Appointment" },
  { label: "Rescheduled", value: "Rescheduled" },
  { label: "Rescheduled Under 24 Hours", value: "Rescheduled Under 24 Hours" },
  { label: "Cancelled", value: "Cancelled" },
  { label: "Cancelled Under 24 hours", value: "Cancelled Under 24 hours" },
  { label: "Another provider was used", value: "Another provider was used" }
];

const fileStatusOptions = [
  {
    label: "Continue care with current provider/follow up provided above is with same provider",
    value: "continue_with_same"
  },
  { label: "This was a one-time assignment", value: "one_time" },
  { label: "Provider is requesting therapy", value: "requesting_therapy" },
  { label: "Follow-up with same provider pending", value: "follow_up_pending" },
  { label: "Released from care", value: "Released from care" },
  { label: "Referred to a different provider", value: "Referred to a different provider" },
  { label: "Other", value: "Other" }
];

// Time options
const hourOptions = Array.from({ length: 12 }, (_, i) => ({
  label: String(i + 1).padStart(2, "0"),
  value: String(i + 1).padStart(2, "0")
}));
const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  label: String(i).padStart(2, "0"),
  value: String(i).padStart(2, "0")
}));
const periodOptions = [
  { label: "AM", value: "AM" },
  { label: "PM", value: "PM" }
];

const getTimeString = (h, m, p) => {
  if (!h || !m || !p) return "";
  return `${h.value}:${m.value} ${p.value}`;
};

const emptyFollowUpLocation = () => ({
  street: "",
  city: "",
  state: "",
  zip: "",
  country: "United States",
  formatted_address: "",
  latitude: null,
  longitude: null,
  place_id: ""
});

const emptyFollowUp = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  date: "",
  hour: null,
  minute: null,
  period: null,
  useSameLocation: null,
  location: emptyFollowUpLocation(),
  available: null
});

/** Convert a click timestamp (Start/Complete Job) into 12-hour parts in local time. */
const parseTimestampToTimeParts = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  const hour24 = date.getHours();
  const minute = date.getMinutes();
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;

  return {
    hour: hour12,
    minute,
    period: hour24 >= 12 ? "PM" : "AM"
  };
};

/** Parse HH:MM / HH:MM:SS schedule strings into 12-hour parts. */
const parseScheduleTimeToParts = (timeSource) => {
  if (!timeSource || typeof timeSource !== "string") return null;

  const timeParts = timeSource.split(":");
  const scheduledHour = parseInt(timeParts[0], 10);
  const scheduledMinute = parseInt(timeParts[1], 10);
  if (Number.isNaN(scheduledHour) || Number.isNaN(scheduledMinute)) return null;

  const hour12 = scheduledHour === 0 ? 12 : scheduledHour > 12 ? scheduledHour - 12 : scheduledHour;

  return {
    hour: hour12,
    minute: scheduledMinute,
    period: scheduledHour >= 12 ? "PM" : "AM"
  };
};

const InterpreterCompletionReport = ({ jobId, jobData, onSubmit, onCancel }) => {
  const fileInputRef = useRef(null);

  // Prefer Start Job click time; fall back to arrival/appointment time.
  const calculateStartTime = () => {
    return (
      parseTimestampToTimeParts(jobData?.in_progress_at || jobData?.job_started_at) ||
      parseScheduleTimeToParts(jobData?.arrival_time || jobData?.scheduled_time)
    );
  };

  // Prefer Complete Job click time.
  const calculateEndTime = () => {
    return parseTimestampToTimeParts(jobData?.job_ended_at || jobData?.completed_at);
  };

  const startTime = calculateStartTime();
  const endTime = calculateEndTime();

  const [formData, setFormData] = useState({
    email: jobData?.assigned_interpreter_email || jobData?.interpreter_email || jobData?.email || "",
    order_number: jobData?.job_number || jobId || "",
    result: null,
    file_status: null,
    notes: ""
  });

  // Helper function to find option by value
  const findOptionByValue = (options, value) => {
    return options.find(option => option.value === value) || null;
  };

  const [startHour, setStartHour] = useState(startTime?.hour != null ? findOptionByValue(hourOptions, String(startTime.hour).padStart(2, "0")) : null);
  const [startMinute, setStartMinute] = useState(startTime?.minute != null ? findOptionByValue(minuteOptions, String(startTime.minute).padStart(2, "0")) : null);
  const [startPeriod, setStartPeriod] = useState(startTime?.period ? findOptionByValue(periodOptions, startTime.period) : null);

  const [endHour, setEndHour] = useState(endTime?.hour != null ? findOptionByValue(hourOptions, String(endTime.hour).padStart(2, "0")) : null);
  const [endMinute, setEndMinute] = useState(endTime?.minute != null ? findOptionByValue(minuteOptions, String(endTime.minute).padStart(2, "0")) : null);
  const [endPeriod, setEndPeriod] = useState(endTime?.period ? findOptionByValue(periodOptions, endTime.period) : null);

  const [rescheduledDate, setRescheduledDate] = useState("");
  const [rescheduledHour, setRescheduledHour] = useState(null);
  const [rescheduledMinute, setRescheduledMinute] = useState(null);
  const [rescheduledPeriod, setRescheduledPeriod] = useState(null);
  const [interpreterAttendedReschedule, setInterpreterAttendedReschedule] = useState(null);

  const [followUps, setFollowUps] = useState(() => [emptyFollowUp()]);

  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const actualDurationMinutes = useMemo(
    () => calculateActualDurationMinutes(startHour, startMinute, startPeriod, endHour, endMinute, endPeriod),
    [startHour, startMinute, startPeriod, endHour, endMinute, endPeriod]
  );

  const showDurationFollowUpNotice = durationExceedsScheduled(actualDurationMinutes, jobData);

  // Prefill start/end from Start Job / Complete Job click times when available
  useEffect(() => {
    if (startTime) {
      setStartHour(findOptionByValue(hourOptions, String(startTime.hour).padStart(2, "0")));
      setStartMinute(findOptionByValue(minuteOptions, String(startTime.minute).padStart(2, "0")));
      setStartPeriod(findOptionByValue(periodOptions, startTime.period));
    }
    if (endTime) {
      setEndHour(findOptionByValue(hourOptions, String(endTime.hour).padStart(2, "0")));
      setEndMinute(findOptionByValue(minuteOptions, String(endTime.minute).padStart(2, "0")));
      setEndPeriod(findOptionByValue(periodOptions, endTime.period));
    }
  }, [
    jobData?.in_progress_at,
    jobData?.job_started_at,
    jobData?.job_ended_at,
    jobData?.completed_at,
    jobData?.arrival_time,
    jobData?.scheduled_time
  ]);

  // Update email when jobData changes
  useEffect(() => {
    const interpreterEmail = jobData?.assigned_interpreter_email || jobData?.interpreter_email || jobData?.email || "";
    setFormData(prev => ({
      ...prev,
      email: interpreterEmail
    }));
  }, [jobData?.assigned_interpreter_email, jobData?.interpreter_email, jobData?.email]);

  useEffect(() => {
    const r = formData.result?.value;
    if (r !== "Rescheduled Under 24 Hours" && r !== "Cancelled Under 24 hours") {
      setInterpreterAttendedReschedule(null);
    }
  }, [formData.result?.value]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const updateFollowUp = (index, patch) => {
    setFollowUps((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const updateFollowUpLocation = (index, locationPatch) => {
    setFollowUps((prev) => prev.map((item, i) => (
      i === index ? { ...item, location: { ...item.location, ...locationPatch } } : item
    )));
  };

  const handleAddressSelect = (index, addressData) => {
    updateFollowUpLocation(index, {
      street: addressData.street || '',
      city: addressData.city || '',
      state: addressData.state || '',
      zip: addressData.zip || '',
      country: addressData.country || 'United States',
      formatted_address: addressData.formatted_address || '',
      latitude: addressData.latitude ?? null,
      longitude: addressData.longitude ?? null,
      place_id: addressData.place_id || ''
    });
  };

  const addFollowUp = () => {
    setFollowUps((prev) => [...prev, emptyFollowUp()]);
  };

  const removeFollowUp = (index) => {
    setFollowUps((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validation
    try {
      // 1. Validate required fields
      if (!formData.email || !formData.order_number) {
        throw new Error("Email and order number are required");
      }

      if (!formData.result) {
        throw new Error("Please select a result");
      }

      if (!formData.file_status) {
        throw new Error("Please select a file status");
      }

      // 2. Validate start and end times
      if (!startHour || !startMinute || !startPeriod) {
        throw new Error("Please select a start time");
      }

      if (!endHour || !endMinute || !endPeriod) {
        throw new Error("Please select an end time");
      }

      // 3. Validate that end time is after start time
      const startTimeString = getTimeString(startHour, startMinute, startPeriod);
      const endTimeString = getTimeString(endHour, endMinute, endPeriod);
      
      // Convert to 24-hour format for comparison
      const convertTo24Hour = (hour, period) => {
        const h = parseInt(hour.value);
        if (period.value === "AM") {
          return h === 12 ? 0 : h;
        } else {
          return h === 12 ? 12 : h + 12;
        }
      };

      const startHour24 = convertTo24Hour(startHour, startPeriod);
      const endHour24 = convertTo24Hour(endHour, endPeriod);
      const startMinutes = startHour24 * 60 + parseInt(startMinute.value);
      const endMinutes = endHour24 * 60 + parseInt(endMinute.value);

      if (endMinutes <= startMinutes) {
        throw new Error("End time must be after start time");
      }

      if (
        (formData.result?.value === "Rescheduled Under 24 Hours" ||
          formData.result?.value === "Cancelled Under 24 hours") &&
        interpreterAttendedReschedule === null
      ) {
        throw new Error("Please indicate whether you attended the appointment");
      }

      // 4a. Validate rescheduled date and time if "Rescheduled Under 24 Hours" is selected
      if (formData.result?.value === "Rescheduled Under 24 Hours") {
        if (!rescheduledDate) {
          throw new Error("New appointment date is required when result is 'Rescheduled Under 24 Hours'");
        }
        if (!rescheduledHour || !rescheduledMinute || !rescheduledPeriod) {
          throw new Error("New appointment time is required when result is 'Rescheduled Under 24 Hours'");
        }
      }

      // 4. Validate follow-up information if "Completed with follow up" is selected
      if (formData.result?.value === "Completed with follow up") {
        if (!followUps.length) {
          throw new Error("At least one follow-up is required");
        }

        const seenSlots = new Set();
        followUps.forEach((followUp, index) => {
          const label = `Follow-up ${index + 1}`;

          if (!followUp.date) {
            throw new Error(`${label}: date is required`);
          }

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const selectedDate = new Date(followUp.date);
          selectedDate.setHours(0, 0, 0, 0);

          if (selectedDate < today) {
            throw new Error(`${label}: date cannot be in the past`);
          }

          if (!followUp.hour || !followUp.minute || !followUp.period) {
            throw new Error(`${label}: time is required`);
          }

          if (followUp.useSameLocation === null) {
            throw new Error(`${label}: please indicate if it is at the same location`);
          }

          if (followUp.useSameLocation === false) {
            const location = followUp.location || {};
            if (!location.street || !location.city || !location.state || !location.zip) {
              throw new Error(`${label}: please provide complete location details`);
            }
          }

          if (followUp.available === null) {
            throw new Error(`${label}: please indicate if you are available`);
          }

          const slot = `${followUp.date}|${getTimeString(followUp.hour, followUp.minute, followUp.period)}`;
          if (seenSlots.has(slot)) {
            throw new Error(`${label}: another follow-up already uses this date and time`);
          }
          seenSlots.add(slot);
        });
      }

      // Build form data after validation passes
      const data = new FormData();
      data.append("email", formData.email);
      data.append("order_number", formData.order_number);
      data.append("start_time", startTimeString);
      data.append("end_time", endTimeString);
      data.append("result", formData.result?.value || "");
      data.append("file_status", formData.file_status?.value || "");
      data.append("notes", formData.notes);

      if (formData.result?.value === "Completed with follow up") {
        const followUpPayload = followUps.map((followUp) => ({
          date: followUp.date,
          time: getTimeString(followUp.hour, followUp.minute, followUp.period),
          use_same_location: followUp.useSameLocation ? "Yes" : "No",
          available: followUp.available ? "Yes" : "No",
          street: followUp.location?.street || "",
          city: followUp.location?.city || "",
          state: followUp.location?.state || "",
          zip: followUp.location?.zip || "",
          country: followUp.location?.country || "United States",
          formatted_address: followUp.location?.formatted_address || "",
          latitude: followUp.location?.latitude ?? null,
          longitude: followUp.location?.longitude ?? null,
          place_id: followUp.location?.place_id || ""
        }));
        data.append("follow_ups", JSON.stringify(followUpPayload));
      }

      if (
        formData.result?.value === "Rescheduled Under 24 Hours" ||
        formData.result?.value === "Cancelled Under 24 hours"
      ) {
        data.append("interpreter_attended", interpreterAttendedReschedule ? "Yes" : "No");
      }

      if (formData.result?.value === "Rescheduled Under 24 Hours" && rescheduledDate) {
        data.append("rescheduled_date", rescheduledDate);
        data.append("rescheduled_time", getTimeString(rescheduledHour, rescheduledMinute, rescheduledPeriod));
      }

      files.forEach((file) => data.append("documents", file));

      const token = localStorage.getItem('interpreterToken');
      const response = await fetch(`${API_BASE}/interpreters/jobs/${jobId}/completion-report`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: data
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Submission failed");
      }

      const responseData = await response.json().catch(() => ({}));
      toast.success("Completion report submitted successfully!");
      if (responseData.durationExceedsScheduled) {
        toast(responseData.facilityDurationFollowUpMessage || FACILITY_DURATION_FOLLOW_UP_MESSAGE, {
          icon: '⏱️',
          duration: 8000,
        });
      }
      
      // Reset form
      setFormData({
        email: jobData?.assigned_interpreter_email || jobData?.interpreter_email || jobData?.email || "",
        order_number: jobData?.job_number || jobId || "",
        result: null,
        file_status: null,
        notes: ""
      });
      setStartHour(null);
      setStartMinute(null);
      setStartPeriod(null);
      setEndHour(null);
      setEndMinute(null);
      setEndPeriod(null);
      setRescheduledDate("");
      setRescheduledHour(null);
      setRescheduledMinute(null);
      setRescheduledPeriod(null);
      setInterpreterAttendedReschedule(null);
      setFollowUps([emptyFollowUp()]);
      setFiles([]);
      
      if (onSubmit) {
        onSubmit();
      }
    } catch (err) {
      console.error("Validation or submission error:", err);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={24} />
        </button>
        
        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-6"
        >
          <div className="text-center">
            <h2 className="text-3xl font-bold text-blue-800 mb-2">
              Assignment Completion Report
            </h2>
            <p className="text-gray-500">
              Fill out the information below to submit the assignment status.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Number *
              </label>
              <input
                type="text"
                name="order_number"
                required
                value={formData.order_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time *
              </label>
              <div className="flex space-x-2">
                <Select
                  value={startHour}
                  onChange={setStartHour}
                  options={hourOptions}
                  placeholder="Hour"
                  className="flex-1"
                  required
                />
                <Select
                  value={startMinute}
                  onChange={setStartMinute}
                  options={minuteOptions}
                  placeholder="Min"
                  className="flex-1"
                  required
                />
                <Select
                  value={startPeriod}
                  onChange={setStartPeriod}
                  options={periodOptions}
                  placeholder="AM/PM"
                  className="flex-1"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time *
              </label>
              <div className="flex space-x-2">
                <Select
                  value={endHour}
                  onChange={setEndHour}
                  options={hourOptions}
                  placeholder="Hour"
                  className="flex-1"
                  required
                />
                <Select
                  value={endMinute}
                  onChange={setEndMinute}
                  options={minuteOptions}
                  placeholder="Min"
                  className="flex-1"
                  required
                />
                <Select
                  value={endPeriod}
                  onChange={setEndPeriod}
                  options={periodOptions}
                  placeholder="AM/PM"
                  className="flex-1"
                  required
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Actual end time. We will pay the minimum agreed rate, but need actual end time for billing purposes.
              </p>
            </div>
          </div>

          {showDurationFollowUpNotice && (
            <FacilityDurationFollowUpNotice
              actualDurationMinutes={actualDurationMinutes}
              jobData={jobData}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Result *
            </label>
            <Select
              value={formData.result}
              onChange={(option) => setFormData({ ...formData, result: option })}
              options={resultOptions}
              placeholder="Select result"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Status *
            </label>
            <Select
              value={formData.file_status}
              onChange={(option) => setFormData({ ...formData, file_status: option })}
              options={fileStatusOptions}
              placeholder="Select file status"
              required
            />
          </div>

          {(formData.result?.value === "Rescheduled Under 24 Hours" ||
            formData.result?.value === "Cancelled Under 24 hours") && (
            <div className="space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-800">
                {formData.result?.value === "Cancelled Under 24 hours"
                  ? "Cancelled under 24 hours"
                  : "Rescheduled appointment"}
              </h3>
              <p className="text-sm text-orange-700">
                {formData.result?.value === "Cancelled Under 24 hours"
                  ? "This appointment was cancelled with less than 24 hours notice."
                  : "This appointment was rescheduled with less than 24 hours notice. Enter the new appointment date and time below."}
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Did you attend this appointment? *
                </label>
                <p className="text-xs text-orange-800 mb-2">
                  If yes, mileage reimbursement on your assignment is kept. If no, it is cleared.
                </p>
                <div className="flex flex-wrap gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="interpreter_attended_short_notice_fe"
                      checked={interpreterAttendedReschedule === true}
                      onChange={() => setInterpreterAttendedReschedule(true)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Yes (keep mileage)</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="interpreter_attended_short_notice_fe"
                      checked={interpreterAttendedReschedule === false}
                      onChange={() => setInterpreterAttendedReschedule(false)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium text-gray-700">No (clear mileage)</span>
                  </label>
                </div>
              </div>

              {formData.result?.value === "Rescheduled Under 24 Hours" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Appointment Date *
                    </label>
                    <input
                      type="date"
                      value={rescheduledDate}
                      onChange={(e) => setRescheduledDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Appointment Time *
                    </label>
                    <div className="flex space-x-2">
                      <Select
                        value={rescheduledHour}
                        onChange={setRescheduledHour}
                        options={hourOptions}
                        placeholder="Hour"
                        className="flex-1"
                      />
                      <Select
                        value={rescheduledMinute}
                        onChange={setRescheduledMinute}
                        options={minuteOptions}
                        placeholder="Min"
                        className="flex-1"
                      />
                      <Select
                        value={rescheduledPeriod}
                        onChange={setRescheduledPeriod}
                        options={periodOptions}
                        placeholder="AM/PM"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {formData.result?.value === "Completed with follow up" && (
            <div className="space-y-4">
              {followUps.map((followUp, index) => (
                <div key={followUp.key} className="space-y-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Follow-up {followUps.length > 1 ? index + 1 : "Details"}
                    </h3>
                    {followUps.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFollowUp(index)}
                        className="inline-flex items-center text-sm text-red-600 hover:text-red-800"
                      >
                        <X size={16} className="mr-1" />
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Date *
                      </label>
                      <input
                        type="date"
                        value={followUp.date}
                        onChange={(e) => updateFollowUp(index, { date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Follow-up Time *
                      </label>
                      <div className="flex space-x-2">
                        <Select
                          value={followUp.hour}
                          onChange={(option) => updateFollowUp(index, { hour: option })}
                          options={hourOptions}
                          placeholder="Hour"
                          className="flex-1"
                        />
                        <Select
                          value={followUp.minute}
                          onChange={(option) => updateFollowUp(index, { minute: option })}
                          options={minuteOptions}
                          placeholder="Min"
                          className="flex-1"
                        />
                        <Select
                          value={followUp.period}
                          onChange={(option) => updateFollowUp(index, { period: option })}
                          options={periodOptions}
                          placeholder="AM/PM"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Use Same Location? *
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name={`useSameLocation-${followUp.key}`}
                          value="yes"
                          checked={followUp.useSameLocation === true}
                          onChange={() => updateFollowUp(index, { useSameLocation: true })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Yes, same location</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name={`useSameLocation-${followUp.key}`}
                          value="no"
                          checked={followUp.useSameLocation === false}
                          onChange={() => updateFollowUp(index, { useSameLocation: false })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">No, different location</span>
                      </label>
                    </div>
                  </div>

                  {followUp.useSameLocation === false && (
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700">
                        New Location Details *
                      </label>
                      <AddressAutocomplete
                        onAddressSelect={(addressData) => handleAddressSelect(index, addressData)}
                        placeholder="Search for an address..."
                        className="w-full"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Street Address"
                          value={followUp.location.street}
                          onChange={(e) => updateFollowUpLocation(index, { street: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="text"
                          placeholder="City"
                          value={followUp.location.city}
                          onChange={(e) => updateFollowUpLocation(index, { city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          type="text"
                          placeholder="State"
                          value={followUp.location.state}
                          onChange={(e) => updateFollowUpLocation(index, { state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="text"
                          placeholder="ZIP Code"
                          value={followUp.location.zip}
                          onChange={(e) => updateFollowUpLocation(index, { zip: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Country"
                          value={followUp.location.country}
                          onChange={(e) => updateFollowUpLocation(index, { country: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          readOnly
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Use the address search above to auto-fill the fields, or enter the address manually.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Are you available for this follow-up? *
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name={`isAvailable-${followUp.key}`}
                          value="yes"
                          checked={followUp.available === true}
                          onChange={() => updateFollowUp(index, { available: true })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name={`isAvailable-${followUp.key}`}
                          value="no"
                          checked={followUp.available === false}
                          onChange={() => updateFollowUp(index, { available: false })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addFollowUp}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <PlusCircle className="h-4 w-4 mr-2 text-gray-500" />
                Add another follow-up
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Only include necessary and important details.
            </p>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Necessary details only (e.g. issues or special circumstances)..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Documents
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center w-full py-3 px-4 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
              >
                <PlusCircle className="h-5 w-5 mr-2 text-gray-400" />
                <span className="text-gray-600">Choose files or drag and drop</span>
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                PDF, DOC, DOCX, JPG, JPEG, PNG up to 10MB each
              </p>
            </div>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterpreterCompletionReport;
